import { signUp, signIn, resetPassword } from "../auth";
import { navigate } from "../router";

export function renderLogin(): string {
  return `
    <div class="auth-page">
      <div class="auth-card">
        <h1 class="auth-title">Log In</h1>
        <p class="auth-subtitle">Welcome back to SideScribe</p>

        <form id="login-form" class="auth-form">
          <div class="form-group">
            <label for="login-email">Email</label>
            <input type="email" id="login-email" placeholder="you@example.com" required />
          </div>
          <div class="form-group">
            <label for="login-password">Password</label>
            <input type="password" id="login-password" placeholder="Your password" required />
          </div>
          <div id="login-error" class="auth-error" style="display:none;"></div>
          <button type="submit" class="btn btn-primary btn-full" id="login-btn">Log In</button>
        </form>

        <div class="auth-links">
          <a href="#" id="forgot-password-link">Forgot password?</a>
          <span class="auth-divider">|</span>
          <a href="#/signup">Create an account</a>
        </div>
      </div>
    </div>
  `;
}

export function mountLogin(): void {
  const form = document.getElementById("login-form") as HTMLFormElement;
  const errorEl = document.getElementById("login-error")!;
  const forgotLink = document.getElementById("forgot-password-link")!;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.style.display = "none";

    const email = (document.getElementById("login-email") as HTMLInputElement).value.trim();
    const password = (document.getElementById("login-password") as HTMLInputElement).value;
    const btn = document.getElementById("login-btn") as HTMLButtonElement;

    btn.disabled = true;
    btn.textContent = "Logging in...";

    try {
      await signIn(email, password);
      navigate("#/dashboard");
    } catch (err: any) {
      errorEl.textContent = getAuthErrorMessage(err.code);
      errorEl.style.display = "block";
    } finally {
      btn.disabled = false;
      btn.textContent = "Log In";
    }
  });

  forgotLink.addEventListener("click", async (e) => {
    e.preventDefault();
    const email = (document.getElementById("login-email") as HTMLInputElement).value.trim();
    if (!email) {
      errorEl.textContent = "Enter your email address, then click 'Forgot password?'";
      errorEl.style.display = "block";
      return;
    }
    try {
      await resetPassword(email);
      errorEl.style.display = "none";
      const successEl = document.createElement("div");
      successEl.className = "auth-success";
      successEl.textContent = "Password reset email sent! Check your inbox.";
      form.appendChild(successEl);
    } catch (err: any) {
      errorEl.textContent = getAuthErrorMessage(err.code);
      errorEl.style.display = "block";
    }
  });
}

export function renderSignup(): string {
  return `
    <div class="auth-page">
      <div class="auth-card">
        <h1 class="auth-title">Create Account</h1>
        <p class="auth-subtitle">Start your free 7-day trial of SideScribe</p>

        <form id="signup-form" class="auth-form">
          <div class="form-group">
            <label for="signup-email">Email</label>
            <input type="email" id="signup-email" placeholder="you@example.com" required />
          </div>
          <div class="form-group">
            <label for="signup-password">Password</label>
            <input type="password" id="signup-password" placeholder="At least 6 characters" required minlength="6" />
          </div>
          <div class="form-group">
            <label for="signup-confirm">Confirm Password</label>
            <input type="password" id="signup-confirm" placeholder="Confirm your password" required minlength="6" />
          </div>
          <div id="signup-error" class="auth-error" style="display:none;"></div>
          <button type="submit" class="btn btn-primary btn-full" id="signup-btn">Create Account</button>
        </form>

        <div class="auth-links">
          <span>Already have an account?</span>
          <a href="#/login">Log in</a>
        </div>
      </div>
    </div>
  `;
}

export function mountSignup(): void {
  const form = document.getElementById("signup-form") as HTMLFormElement;
  const errorEl = document.getElementById("signup-error")!;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.style.display = "none";

    const email = (document.getElementById("signup-email") as HTMLInputElement).value.trim();
    const password = (document.getElementById("signup-password") as HTMLInputElement).value;
    const confirm = (document.getElementById("signup-confirm") as HTMLInputElement).value;
    const btn = document.getElementById("signup-btn") as HTMLButtonElement;

    if (password !== confirm) {
      errorEl.textContent = "Passwords do not match.";
      errorEl.style.display = "block";
      return;
    }

    btn.disabled = true;
    btn.textContent = "Creating account...";

    try {
      await signUp(email, password);
      navigate("#/dashboard");
    } catch (err: any) {
      errorEl.textContent = getAuthErrorMessage(err.code);
      errorEl.style.display = "block";
    } finally {
      btn.disabled = false;
      btn.textContent = "Create Account";
    }
  });
}

function getAuthErrorMessage(code: string): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    default:
      return "An error occurred. Please try again.";
  }
}
